import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, PackageCheck, Scissors, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loading } from "@/components/Common";
import { StatusBadge, WarehouseBadge } from "@/components/Badges";
import { SimpleSelect } from "@/components/Filters";
import { ReceiveDialog } from "@/components/ReceiveDialog";
import { ReasonDialog, ReviseDialog } from "@/components/po/PoDialogs";
import { ReceiptHistory, ReceiptMatrix } from "@/components/po/ReceiptHistory";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api, errMsg, notifyStockChanged } from "@/lib/api";
import { dateLabel } from "@/lib/format";

const Info = ({ label, children }) => <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-sm font-medium">{children}</div></div>;

const LinesTable = ({ items }) => (
  <div className="overflow-x-auto"><table className="data-table" data-testid="po-lines-table">
    <thead><tr><th>Item</th><th>Ordered</th><th>Received</th><th>Short-closed</th><th>Pending</th><th>% done</th></tr></thead>
    <tbody>{items.map((i, idx) => {
      const pend = Math.max(i.qty - (i.received || 0) - (i.short_closed_qty || 0), 0);
      const pct = i.qty ? Math.round((100 * Math.min(i.received || 0, i.qty)) / i.qty) : 0;
      return (
        <tr key={i.product_id} data-testid={`po-line-${idx}`}>
          <td className="wrap-cell min-w-[260px]"><div className="product-name">{i.name}</div>{i.sku && <div className="font-mono text-[11px] text-muted-foreground">SKU: {i.sku}</div>}</td>
          <td className="font-mono">{i.qty}{i.original_qty && i.original_qty !== i.qty && <div className="text-[10px] text-amber-600">orig {i.original_qty}</div>}</td>
          <td className="font-mono" data-testid={`po-line-received-${idx}`}>{i.received || 0}</td>
          <td className="font-mono">{i.short_closed_qty || ""}{i.short_close_reason && <div className="wrap-cell text-[10px] text-muted-foreground">{i.short_close_reason}</div>}</td>
          <td className={`font-mono font-semibold ${pend ? "text-amber-600" : "text-emerald-600"}`} data-testid={`po-line-pending-${idx}`}>{pend}</td>
          <td className="min-w-[120px]"><div className="h-2 rounded bg-muted"><div className="h-2 rounded bg-primary" style={{ width: `${pct}%` }} /></div><div className="font-mono text-[10px]">{pct}%</div></td>
        </tr>);
    })}</tbody>
  </table></div>
);

export default function PoDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can("orders", "edit");
  const { data, reload } = useLoad(() => api.get(`/po-receipts/po/${id}`).then((r) => r.data).catch((e) => ({ error: errMsg(e) })), [id]);
  const [dlg, setDlg] = useState("");
  const [grn, setGrn] = useState(null);
  const [scope, setScope] = useState("all");
  const [matrix, setMatrix] = useState(false);
  if (!data) return <Loading />;
  if (data.error) return (
    <div className="py-16 text-center" data-testid="po-detail-error"><div className="mb-3 text-sm text-muted-foreground">{data.error}</div>
      <Link to="/po-receipts?tab=open" className="text-sm text-primary hover:underline">Back to PO Receipts</Link></div>
  );
  const { order: po, summary: s, receipts } = data;
  const open = ["pending", "partial"].includes(po.status);
  const done = () => { reload(); notifyStockChanged(); };
  const shortClose = async (reason) => {
    await api.post(`/purchase-orders/${po.id}/short-close`, { expected_rev: po.rev || 0, reason, product_id: scope === "all" ? null : scope });
    toast.success("Balance short closed"); done();
  };
  const cancelGrn = async (reason) => {
    await api.post(`/po-receipts/${grn.id}/cancel`, { reason });
    toast.success(`${grn.receipt_no} cancelled${grn.is_opening ? "" : " — stock IN reversed"}`); done();
  };
  const pendingLines = po.items.filter((i) => i.qty - (i.received || 0) - (i.short_closed_qty || 0) > 0);
  return (
    <div data-testid="po-detail-page">
      <Link to="/po-receipts?tab=open" className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" data-testid="po-detail-back"><ArrowLeft className="h-3.5 w-3.5" /> PO Receipts</Link>
      <PageHeader title={po.order_no} subtitle={`${po.kind === "manufacturing" ? "Manufacturing" : "Purchase"} order · ${po.supplier || "No vendor"}`}>
        {open && canEdit && <>
          <Button size="sm" onClick={() => setDlg("receive")} data-testid="po-detail-record-btn"><PackageCheck className="h-4 w-4" /> Record receipt</Button>
          <Button size="sm" variant="outline" onClick={() => setDlg("revise")} data-testid="po-detail-revise-btn"><Pencil className="h-4 w-4" /> Revise ordered qty</Button>
          <Button size="sm" variant="outline" onClick={() => { setScope("all"); setDlg("short"); }} data-testid="po-detail-short-btn"><Scissors className="h-4 w-4" /> Short close balance</Button>
        </>}
      </PageHeader>
      <div className="panel mb-5 grid grid-cols-2 gap-4 p-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="po-detail-header">
        <Info label="Status"><StatusBadge value={po.status} />{s.overdue && <span className="ml-1 rounded border border-red-200 bg-red-50 px-1.5 text-[10px] font-semibold uppercase text-red-600" data-testid="po-detail-overdue">overdue</span>}</Info>
        <Info label="PO date">{dateLabel(po.order_date)}</Info>
        <Info label="Due date"><span className={s.overdue ? "text-red-600" : ""}>{po.expected_date ? dateLabel(po.expected_date) : "—"}</span></Info>
        <Info label="Warehouse"><WarehouseBadge value={po.warehouse} /></Info>
        <Info label="Ordered"><span className="font-mono">{s.ordered}</span></Info>
        <Info label="Received"><span className="font-mono" data-testid="po-detail-received">{s.received}</span></Info>
        <Info label="Pending"><span className="font-mono" data-testid="po-detail-pending">{s.pending}</span></Info>
      </div>
      <div className="panel mb-5 p-4"><LinesTable items={po.items} /></div>
      <div className="panel p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">Receipt history</div>
          <label className="flex items-center gap-2 text-xs">Matrix view <Switch checked={matrix} onCheckedChange={setMatrix} data-testid="po-matrix-toggle" /></label>
        </div>
        {matrix ? <ReceiptMatrix order={po} receipts={receipts} /> : <ReceiptHistory receipts={receipts} canEdit={canEdit} onCancel={(g) => { setGrn(g); setDlg("cancel"); }} />}
      </div>
      <ReceiveDialog open={dlg === "receive"} onOpenChange={(o) => !o && setDlg("")} order={po} onDone={reload} />
      <ReviseDialog open={dlg === "revise"} onOpenChange={(o) => !o && setDlg("")} order={po} onDone={reload} />
      <ReasonDialog open={dlg === "short"} onOpenChange={(o) => !o && setDlg("")} title="Short close balance" testid="short-close-dialog" confirmLabel="Short close"
        description="The vendor will not supply the remaining quantity. Pending becomes 0 for the chosen lines. Stock does not change." onConfirm={shortClose}>
        <SimpleSelect value={scope} onChange={setScope} testid="short-close-scope" className="h-9 w-full"
          options={[["all", "Whole order — all pending lines"], ...pendingLines.map((i) => [i.product_id, `${i.name} (pending ${i.qty - (i.received || 0) - (i.short_closed_qty || 0)})`])]} />
      </ReasonDialog>
      <ReasonDialog open={dlg === "cancel"} onOpenChange={(o) => !o && setDlg("")} title={`Cancel ${grn?.receipt_no || ""}`} testid="cancel-grn-dialog" confirmLabel="Cancel GRN" onConfirm={cancelGrn}
        description={(grn?.is_opening ? "Opening balance receipts never changed stock, so no stock is reversed." : "Its stock IN will be reversed with a stock OUT entry dated today. Record a new GRN afterwards if needed.")
          + (["closed", "received"].includes(po.status) ? " This order will re-open with the cancelled quantity pending again." : "")} />
    </div>
  );
}
